import {Observable} from 'rxjs/Observable';
import { empty } from 'rxjs/observable/empty';
import includes from 'lodash/includes';
import get from 'lodash/get';
import {toggleSort} from 'utils/DBUtils';
import {filterOptionsFactory, sortOptionsFactory} from 'viewOptions/factories';
import {createResourcePageSelector} from 'resources/selectors';
import {getResourceAction, getResourcePageAction, Types as ResourceActionTypes} from 'resources/actions';
import * as LegacyUGCApi from '../../api/ugc-legacy';
import * as ResourceApi from '../../api/resources';
import {
    auditSuccessfulAction,
    emailSentAction,
    failedToAuditSubmissionAction,
    getSubmissionsAction,
    setCampaignsAction,
    receivedSearchQueryAction,
    sendSearchQueryAction,
    Types
} from './actions';
import {UGC_PLATFORMS} from './utils';
import {Actions as ServiceActions, performAction} from "../../api/services";
import {clone, isBlank, isEmpty, isNoU, isTruthy} from "../../utils/DBUtils";
import moment from "moment";

const handleErrorAction = error => Observable.of({type: 'ERROR', error});

const pageSize = 20;
const sortOptions = sortOptionsFactory('_created');
sortOptions.sorts = toggleSort(sortOptions.sorts, '_created');

const getResourcePage = createResourcePageSelector(ResourceApi.Types.UGCSubmission); // TODO: this is hard-coded to UGC

const fetchCurrentSubmissionsPage = state => () => {
  const {currentMasterCampaign, currentPlatforms} = state.ugc;
  const {page} = getResourcePage(state.resources);
  return getSubmissionsAction(currentMasterCampaign, state.ugcResourceType, page, currentPlatforms);
};


const fetchCampaignEpic = (action$, store) => action$.ofType(Types.GET_CAMPAIGNS)
    .map((action) => {
        const masterCampaignId = action.masterCampaignId || state.ugc.currentMasterCampaign;
        return getResourceAction(ResourceApi.Types.Campaign, masterCampaignId);
    })
    

const receiveCampaignsEpic = (action$, store) => action$.ofType(ResourceActionTypes.RECEIVED_RESOURCE)
    .filter(action => (action.resourceType === ResourceApi.Types.Campaign))
    .map((action) => {
    const masterCampaignId = action.resource.id;
    const campaigns = action.resource.campaigns;

    return setCampaignsAction(masterCampaignId, campaigns);
  })


  // the filter modal triggers GET_SUBMISSIONS
  const fetchSubmissionsEpic = (action$, store) => action$.ofType(Types.SET_CAMPAIGNS,   Types.GET_SUBMISSIONS,
    )
    .map(action => {
    const state = store.getState();
    
    const masterCampaignId = action.masterCampaignId || state.ugc.currentMasterCampaign;

    // filtering by platform is subtractive: if none has been selected, then fetch for all platforms.

      let additionalFilters = clone(action.additionalFilters || state.ugc.additionalFilters || {});

      const platforms = get(additionalFilters, 'platforms', []).length
          ? additionalFilters.platforms : (
              state.ugc.currentPlatforms.length
                  ? state.ugc.currentPlatforms
                  : UGC_PLATFORMS
          );

      const campaignIds = state.ugc.campaigns[masterCampaignId];

    let filters = [{
        field: 'campaignId',
        operator: 'In',
        value: Object.keys(campaignIds)
            .filter(platform => includes(platforms, platform))
            .map(platform => campaignIds[platform])
            .join(',')
    }];


    // platforms are resolved above by campaignId
    delete additionalFilters.platforms;

    let searchQuery = null;
    if (!isNoU(additionalFilters.searchQuery) && additionalFilters.searchQuery.trim() !== "") {
        searchQuery = additionalFilters.searchQuery;

        // Don't include search query in filters
        delete additionalFilters.searchQuery;
    }

    // transform form values in to filters
    additionalFilters = Object.keys(additionalFilters)
        .filter((key) => isTruthy(additionalFilters[key]))
        .map((key) => {
            const value = additionalFilters[key];
            if (Array.isArray(value)) {
                return {
                    field: key,
                    operator: "In",
                    value: value.join(',')
                }
            }

            if (key === "fromDate" || key === "toDate") {
                const dateOperators = {
                    fromDate: "GreaterThanOrEqual",
                    toDate: "LessThanOrEqual"
                };

                return {
                    field: "_created",
                    operator: dateOperators[key],
                    value: moment(value).unix() * 1000
                }
            }

            return {
                field: key,
                operator: "Equal",
                value: value
            }
        });

    additionalFilters = additionalFilters.filter((filter) => isTruthy(filter.value) && !isBlank(filter.value));

    if (additionalFilters) {
        for (let filter of additionalFilters) {
            filters.push(filter);
        }
    }

    // for checking if a campaign's id is "In" a comma-delimited string of ids for the currently
    // selected platforms.
    const filterOptions = filterOptionsFactory({
      filters: filters
    });

    if (!isNoU(searchQuery)) {
        return sendSearchQueryAction(searchQuery, filters);
    }
    
    return getResourcePageAction(state.ugc.ugcResourceType, pageSize, action.page, sortOptions, filterOptions);
  });

const sendSearchQueryEpic = (action$, store) => action$.ofType(Types.SEND_SEARCH_QUERY)
    .mergeMap((action) => {
        return ResourceApi.searchResources(state.ugc.ugcResourceType, action.searchQuery, sortOptions, action.filters);
    })
    .map(({response: {object}}) => receivedSearchQueryAction(object));

const approveSubmissionEpic = (action$, store) => action$.ofType(Types.APPROVE_SUBMISSION)
  .mergeMap(action => {
    const approveSubmissionRequest$ = LegacyUGCApi.approveSubmission(action.id);

    return approveSubmissionRequest$
      .map(fetchCurrentSubmissionsPage(store.getState()))
      .catch(handleErrorAction);
  });

const unapproveSubmissionEpic = (action$, store) => action$.ofType(Types.UNAPPROVE_SUBMISSION)
  .mergeMap(action => {
    const unapproveSubmissionRequest$ = LegacyUGCApi.unapproveSubmission(action.id);

    return unapproveSubmissionRequest$
      .map(fetchCurrentSubmissionsPage(store.getState()))
      .catch(handleErrorAction);
  });

const deleteSubmissionEpic = (action$, store) => action$.ofType(Types.DELETE_SUBMISSION)
  .mergeMap(action => {
    const deleteSubmissionRequest$ = LegacyUGCApi.deleteSubmission(action.id);

    return deleteSubmissionRequest$
      .map(fetchCurrentSubmissionsPage(store.getState()))
      .catch(handleErrorAction);
  });

const favoriteSubmissionEpic = (action$, store) => action$.ofType(Types.FAVORITE_SUBMISSION)
  .mergeMap(action => {
    const favoriteSubmissionRequest$ = LegacyUGCApi.favoriteSubmission(action.id);

    return favoriteSubmissionRequest$
      .map(fetchCurrentSubmissionsPage(store.getState()))
      .catch(handleErrorAction);
  });

const unfavoriteSubmissionEpic = (action$, store) => action$.ofType(Types.UNFAVORITE_SUBMISSION)
  .mergeMap(action => {
    const unfavoriteSubmissionRequest$ = LegacyUGCApi.unfavoriteSubmission(action.id);

    return unfavoriteSubmissionRequest$
      .map(fetchCurrentSubmissionsPage(store.getState()))
      .catch(handleErrorAction);
  });

const approveReceiptEpic = (action$, store) => action$.ofType(Types.APPROVE_RECEIPT)
    .mergeMap(action => {
        const state = store.getState();
        return performAction(state.ugc.ugcResourceType, ServiceActions.ApproveSubmission, {submissionId: action.id})
            .map(() => auditSuccessfulAction(action.id, "Approved"))
            .catch((error) => Observable.of(failedToAuditSubmissionAction(action.id, error.response.errorCode)));
    });

const rejectReceiptEpic = (action$, store) => action$.ofType(Types.REJECT_RECEIPT)
    .mergeMap(action => {
        const state = store.getState();
        return performAction(state.ugc.ugcResourceType, ServiceActions.RejectSubmission, {submissionId: action.id, silent: action.silent, reason: action.reason, message: action.message })
            .map(() => auditSuccessfulAction(action.id, "Rejected"))
            .catch((error) => Observable.of(failedToAuditSubmissionAction(action.id, error.response.errorCode)));
    });

const resendEmailEpic = (action$, store) => action$.ofType(Types.RESEND_EMAIL)
    .mergeMap(action => {
        const state = store.getState();
        return performAction(state.ugc.ugcResourceType, ServiceActions.ResendPrizeForSubmission, {submissionId: action.id})
            .map(() => emailSentAction(action.id))
            .catch((error) => Observable.of(failedToAuditSubmissionAction(action.id, error.response.errorCode)));
    });

export default [
  fetchCampaignEpic,
  receiveCampaignsEpic,
  fetchSubmissionsEpic,
  approveSubmissionEpic,
  unapproveSubmissionEpic,
  deleteSubmissionEpic,
  favoriteSubmissionEpic,
  unfavoriteSubmissionEpic,
    approveReceiptEpic,
    rejectReceiptEpic,
    resendEmailEpic,
    sendSearchQueryEpic
];



// WEBPACK FOOTER //
// ./public/pages/UGCModeration/epics.js